import {
    Box, Button, Card, CardContent, IconButton, Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { FilterSection } from '../FilterSection';
import { WidgetOption } from './WidgetSelector';
import { ChipsInput } from '../ChipsInput';

type InlineRanking = Models.SetsPlayground.InlineSetRanking;
type SetFilterRequest = Models.SetsPlayground.SetFilterRequest;

interface Props {
    value: InlineRanking;
    onChange: (next: InlineRanking) => void;
}

const InlineRankingWidgetComponent = ({ value, onChange }: Props): JSX.Element => {
    const elementFilters = value.elementFilters || [];

    const handleAddElementFilter = (): void => {
        const newFilter: SetFilterRequest = {};

        onChange({
            ...value,
            elementFilters: [...elementFilters, newFilter],
        });
    };

    const handleRemoveElementFilter = (index: number): void => {
        const updatedFilters = elementFilters.filter((_, i) => i !== index);

        onChange({
            ...value,
            elementFilters: updatedFilters,
        });
    };

    const handleUpdateElementFilter = (index: number, filter: SetFilterRequest): void => {
        const updatedFilters = elementFilters.map((f, i) => (i === index ? filter : f));

        onChange({
            ...value,
            elementFilters: updatedFilters,
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <ChipsInput
                label="Design Concept IDs"
                values={value.designConceptIds}
                onChange={(newIds): void => onChange({ ...value, designConceptIds: newIds })}
            />
            <Box>
                <Typography fontWeight="medium" sx={{ mb: 2 }} variant="body2">
                    Element Filters
                </Typography>
                {elementFilters.length === 0 && (
                    <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                        No element filters added yet. Add filters to refine each element in the inline ranking.
                    </Typography>
                )}
                {elementFilters.map((filter, index) => (
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography fontWeight="medium" sx={{ flex: 1 }} variant="body2">
                                    Element Filter
                                    {' '}
                                    {index + 1}
                                </Typography>
                                <IconButton
                                    aria-label="remove filter"
                                    color="error"
                                    size="small"
                                    onClick={(): void => handleRemoveElementFilter(index)}
                                >
                                    <DeleteOutline fontSize="small" />
                                </IconButton>
                            </Box>
                            <FilterSection
                                filter={filter}
                                showTitle={false}
                                onChangeFilter={
                                    (updatedFilter): void => handleUpdateElementFilter(index, updatedFilter)
                                }
                            />
                        </CardContent>
                    </Card>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button startIcon={<Add />} variant="outlined" onClick={handleAddElementFilter}>
                        Add Element Filter
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export const InlineRankingWidget: WidgetOption<InlineRanking> = {
    label: 'Inline Ranking',
    value: 'inline',
    helperText: 'Provide a list of design concept IDs one by one.',
    component: InlineRankingWidgetComponent,
    getDefaultValue: () => ({
        type: 'inline',
        designConceptIds: [],
        elementFilters: [],
    }),
};
